import { Bucket, BucketCounts } from '../../services/executionStatsService';
import { NOT_TRACKED } from '../CaseWorkflow/caseWorkflowVocabulary';
import { useThemeMode } from '../../theme/ThemeModeProvider';

export { NOT_TRACKED };

/**
 * Plain-language vocabulary for run statistics.
 *
 * No component writes a raw orchestrator status or a bucket name as a literal;
 * they read the label, definition and colour from here.
 */

/** Stack order, bottom up. Also the order of every legend and KPI row. */
export const BUCKET_ORDER: Bucket[] = ['success', 'failed', 'pendingReview', 'running', 'stalled'];

export const BUCKET_LABELS: Record<Bucket, string> = {
  success: 'Succeeded',
  failed: 'Failed',
  pendingReview: 'Pending review',
  running: 'Running',
  stalled: 'Stalled'
};

/** Short forms for the compact badge. */
export const BUCKET_SHORT: Record<Bucket, string> = {
  success: '✓',
  failed: '✗',
  pendingReview: 'review',
  running: 'running',
  stalled: 'stalled'
};

export const bucketDefinition = (bucket: Bucket, staleAfterHours: number): string => {
  switch (bucket) {
    case 'success':
      return 'Runs that finished without an error.';
    case 'failed':
      return 'Runs that ended in an error.';
    case 'pendingReview':
      return 'Runs waiting on a person to review a decision before they can continue.';
    case 'running':
      return `Runs still in progress that started within the last ${staleAfterHours} hours.`;
    case 'stalled':
      return `Runs still marked in progress more than ${staleAfterHours} hours after they started. ` +
        'They most likely stopped without recording an outcome, so treat them like failures until checked.';
  }
};

/**
 * Why a pending-review count is missing once the platform records reviews: the
 * window reaches back before it started to. Null while it doesn't record them.
 */
export const trackedFromNote = (runtimeStatusSince: string | null | undefined): string | null =>
  runtimeStatusSince
    ? `Counted for runs from ${new Date(runtimeStatusSince).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric'
    })}`
    : null;

export const TOTAL_DEFINITION = 'Every run that started in this window, whatever its outcome.';

export const FAILURE_RATE_DEFINITION =
  'Failed runs as a share of finished runs (succeeded + failed). Runs still in progress are left out.';

/** Shown under a campaign's numbers when they come from the flow instead. */
export const FLOW_ATTRIBUTION =
  'All runs of this workflow — this campaign\'s runs aren\'t tagged with it yet.';

/**
 * Status colours, validated with the dataviz validator against the chart
 * surface (#112240, dark), in BUCKET_ORDER:
 *   CVD separation PASS (worst adjacent failed-success ΔE 12.4 protan)
 *   normal-vision PASS (worst ΔE 21.5) · contrast PASS (all >= 3:1)
 * The lightness-band and chroma checks fail by design: these are status steps,
 * not categorical ones, and success is deliberately grey so failures stand out.
 * Pending review and stalled (both warm) must never be adjacent — side by side
 * they measure ΔE 13.6, under the normal-vision floor.
 */
export const BUCKET_COLOURS: Record<Bucket, string> = {
  success: '#64748B',
  failed: '#D03B3B',
  pendingReview: '#FAB219',
  running: '#38BDF8',
  stalled: '#EC835A'
};

/**
 * The same steps for a white surface, in BUCKET_ORDER:
 *   CVD separation PASS (worst adjacent failed-pending ΔE 11.2 deutan)
 *   normal-vision PASS (worst ΔE 18.0) · contrast WARN: pending review 2.94:1,
 *   so it is never shown without its label or a legend.
 * Same design failures as above (success stays grey), same adjacency rule.
 */
const BUCKET_COLOURS_LIGHT: Record<Bucket, string> = {
  success: '#64748B',
  failed: '#D03B3B',
  pendingReview: '#CA8A04',
  running: '#0284C7',
  stalled: '#EA580C'
};

/** The bucket colours for the theme currently painted. */
export const useBucketColours = (): Record<Bucket, string> =>
  useThemeMode().resolved === 'light' ? BUCKET_COLOURS_LIGHT : BUCKET_COLOURS;

/** Text colour for a count in a badge: status colour only when it needs attention. */
export const countInk = (bucket: Bucket, value: number | null): string => {
  if (value === null || value === 0) return '#64748B';
  if (bucket === 'failed') return '#F87171';
  if (bucket === 'stalled' || bucket === 'pendingReview') return '#FBBF24';
  return '#CBD5E1';
};

/** The buckets that ask something of a person, in the order they are listed. */
export type AttentionBucket = Extract<Bucket, 'failed' | 'stalled' | 'pendingReview'>;

export const ATTENTION_BUCKETS: AttentionBucket[] = ['failed', 'stalled', 'pendingReview'];

/**
 * The run buckets the project page still flags: runs that broke. A run waiting
 * for review is not the signal there — the decision it waits on is, and the
 * page lists those by case (DecisionsWaiting), so flagging the run as well would
 * count one decision twice under two names.
 */
export const OPERATIONS_BUCKETS: AttentionBucket[] = ['failed', 'stalled'];

/** "2 failed", "1 pending review" — a count's word, lower case, for a chip. */
export const ATTENTION_SHORT: Record<AttentionBucket, string> = {
  failed: 'failed',
  stalled: 'stalled',
  pendingReview: 'pending review'
};

/** A chip's colour: the badge ink a non-zero count of this bucket gets. */
export const attentionInk = (bucket: AttentionBucket): string => countInk(bucket, 1);

export const count = (value: number): string => value.toLocaleString();

export const percent = (value: number | null): string =>
  value === null ? '—' : `${(value * 100).toFixed(value > 0 && value < 0.1 ? 1 : 0)}%`;

/** Failed over finished runs, the same rule the API applies. */
export const failureRate = (counts: BucketCounts): number | null => {
  const finished = counts.success + counts.failed;
  return finished > 0 ? counts.failed / finished : null;
};

export const duration = (ms: number | null): string => {
  if (ms === null) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

export const relativeTime = (iso: string | null, now = Date.now()): string => {
  if (!iso) return 'never';
  const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};
